import React from "react";
import { Sparkle, ArrowRight } from "lucide-react";
import { useSiteContent, txt } from "../hooks/useSiteContent";

export default function Hero() {
  const content = useSiteContent();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full bg-[#050505] overflow-hidden grain"
    >
      {/* Issue label — magazine masthead */}
      <div className="absolute top-20 md:top-24 left-6 md:left-12 z-20 flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-white/60">
        <span>Issue №01</span>
        <span className="w-8 h-px bg-white/30" />
        <span>NYC · 2026</span>
      </div>

      <div className="hidden lg:block absolute top-24 right-12 z-20 text-[10px] uppercase tracking-[0.35em] text-white/60 text-right max-w-[280px] leading-relaxed">
        PR · Media · Influencer Partnerships
        <br />
        Branding · Digital Growth
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24 relative">
        {/* Editorial content (centered, max-width) */}
        <div className="max-w-4xl xl:max-w-5xl mx-auto text-center">
          <p
            data-testid="hero-overline"
            className="nova-overline mb-6 md:mb-8"
          >
            ✦ A premium PR &amp; growth advisory — New York
          </p>

          <h1
            data-testid="hero-headline"
            className="font-display text-white leading-[0.95] tracking-[-0.02em] text-balance"
          >
            <span className="block text-[11vw] sm:text-[9vw] md:text-[6.5vw] lg:text-[5vw] xl:text-[5.25rem] 2xl:text-[5.75rem] font-light">
              {txt(content, "hero.headline_1", "Authority.")}
            </span>
            <span className="block text-[11vw] sm:text-[9vw] md:text-[6.5vw] lg:text-[5vw] xl:text-[5.25rem] 2xl:text-[5.75rem] font-light italic text-[#7FB3D5]">
              {txt(content, "hero.headline_2", "Visibility.")}
            </span>
            <span className="flex items-baseline justify-center gap-2 md:gap-3 lg:gap-4 text-[11vw] sm:text-[9vw] md:text-[6.5vw] lg:text-[5vw] xl:text-[5.25rem] 2xl:text-[5.75rem] font-light">
              {txt(content, "hero.headline_3", "Strategic")}
              <Sparkle
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-10 lg:h-10 xl:w-11 xl:h-11 text-[#7FB3D5] -translate-y-1 md:-translate-y-1.5 shrink-0"
                fill="#7FB3D5"
                strokeWidth={0.8}
              />
            </span>
            <span className="block text-[11vw] sm:text-[9vw] md:text-[6.5vw] lg:text-[5vw] xl:text-[5.25rem] 2xl:text-[5.75rem] font-light">
              {txt(content, "hero.headline_4", "Growth.")}
            </span>
          </h1>

          <div className="mt-10 md:mt-14 max-w-2xl mx-auto">
            <p
              data-testid="hero-subtitle"
              className="text-base md:text-lg text-white/70 leading-relaxed font-light"
            >
              {txt(
                content,
                "hero.subtitle",
                "NOVA Media PR builds premium visibility for founders, creators, experts, investors, and brands — through strategic public relations, influencer partnerships, brand positioning, digital marketing, launches, activations, and growth consulting."
              )}
            </p>

            <p
              data-testid="hero-trust"
              className="mt-6 text-sm md:text-base font-display italic text-[#7FB3D5]/90"
            >
              {txt(content, "hero.trust", "Your brand deserves to be seen, heard, and recognized.")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center sm:justify-center">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#7FB3D5] text-black text-[11px] uppercase tracking-[0.3em] hover:bg-white transition-colors duration-500"
              >
                Book a 30-minute consultation
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
              </a>
              <a
                href="#services"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/70 hover:text-[#7FB3D5] transition-colors duration-300"
              >
                Explore services
                <span className="w-8 h-px bg-current" />
              </a>
            </div>
          </div>
        </div>

        {/* Intro section — narrative bridge */}
        <div className="mt-20 md:mt-32 grid grid-cols-12 gap-6 md:gap-8 border-t border-white/10 pt-12 md:pt-16">
          <div className="col-span-12 md:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
              ✦ The starting point
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p
              data-testid="hero-intro"
              className="font-display text-2xl md:text-3xl lg:text-4xl text-white font-light leading-[1.25] tracking-tight"
            >
              In today&apos;s market, attention is not enough. Brands need
              <span className="italic text-[#7FB3D5]"> credibility</span>,
              positioning, and the right media narrative to become impossible
              to ignore. NOVA Media PR helps you shape that narrative, build
              authority, and transform visibility into{" "}
              <span className="italic text-[#7FB3D5]">strategic growth</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
